import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { useGlobalConfig } from 'context/GlobalContext';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useToast } from 'context/ToastContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { PilotApis } from 'apis/pilotAPI';
import { PromotionFilter } from './PromotionFilter';
import { buildShellBundlePayload } from './utils/shellBundlePayload';
import { formatLevelDisplay } from './utils/levelDisplay';
import studioStyles from '../environments/StudioModal.module.scss';

const SESSION_KEY = 'pending_config_promotion';

type DeploymentAction = 'PUSH' | 'CREATE_PR';

type TargetEnvironment = { id: number; environmentName: string; type: string; stackName?: string };
type TargetEntry = { envId: number; environmentName: string; type: string; targetValue: any; status: string };

type PendingPromotionPayload = {
  projectId: string;
  items: Array<{
    category: string;
    key: string;
    status: string;
    sourceValue: any;
    targetValue: any;
    metadata?: Record<string, any>;
    conflictReason?: string | null;
    targets?: TargetEntry[];
  }>;
  sourceEnvId: number;
  targetEnvId: number;
  deploymentAction: DeploymentAction;
  sourceEnvLabel?: string;
  targetEnvLabel?: string;
  projectName?: string;
  targetEnvironments?: TargetEnvironment[];
};

function safeStringify(v: any): string {
  if (v === null || typeof v === 'undefined') return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/** For config keys in "configType::configKey" format, return only the configKey part. */
function extractConfigKeyForDisplay(raw: string): string {
  if (!raw || typeof raw !== 'string') return raw ?? '';
  const idx = raw.indexOf('::');
  return idx >= 0 ? raw.slice(idx + 2) : raw;
}

/** Same naming convention as env list. Uses API keys: environment_name, stack_name, type. */
function getEnvListDisplayLabel(te: { environmentName?: string; environment_name?: string; id?: number; type?: string; stack_name?: string; stackName?: string } | undefined, projectName: string): string {
  if (!te) return '';
  const baseName = String(te.environment_name ?? te.environmentName ?? te.id ?? '').trim();
  const envPart = baseName ? baseName.toUpperCase() : '';
  const projectPart = (projectName || '').trim();
  let core = projectPart ? `${envPart}_${projectPart}` : envPart;
  const type = String(te.type ?? '').trim().toLowerCase();
  const stackName = String(te.stack_name ?? te.stackName ?? '').trim();
  if (type === 'dev') {
    core = `DEV_${core}`;
    if (stackName) core += ` (${stackName})`;
  } else if (type === 'uat') {
    core = `UAT_${core}`;
    if (stackName) core += ` (${stackName})`;
  }
  return core;
}

type StudioButtonSize = 'sm' | 'md';

function StudioButton({
  children,
  onClick,
  disabled,
  variant = 'primary',
  size = 'md',
  startIcon,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'outline';
  size?: StudioButtonSize;
  startIcon?: React.ReactNode;
}) {
  const className =
    variant === 'primary'
      ? `${studioStyles.button} ${studioStyles.primaryButton}`
      : `${studioStyles.button} ${studioStyles.outlineButton}`;
  const style: React.CSSProperties | undefined =
    size === 'sm' ? { height: 32, padding: '0 12px', fontSize: 13 } : undefined;
  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled} style={style}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {startIcon ? <span style={{ display: 'inline-flex', alignItems: 'center' }}>{startIcon}</span> : null}
        {children}
      </span>
    </button>
  );
}

type SectionKey = string;

export const ConfigCompareReviewPage = () => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const theme = useTheme();
  const rcRef = useRef(rc);
  useEffect(() => {
    rcRef.current = rc;
  }, [rc]);

  const [payload, setPayload] = useState<PendingPromotionPayload | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectionKey, setSectionKey] = useState<SectionKey>('');
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterLevel, setFilterLevel] = useState<string[]>([]);
  const [filterConfigType, setFilterConfigType] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [shellBundle, setShellBundle] = useState<any | null>(null);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) {
        setRedirecting(true);
        rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE);
        return;
      }
      const parsed = JSON.parse(raw) as PendingPromotionPayload;
      if (
        !parsed.projectId ||
        !Array.isArray(parsed.items) ||
        parsed.items.length === 0 ||
        typeof parsed.sourceEnvId !== 'number' ||
        typeof parsed.targetEnvId !== 'number' ||
        !parsed.deploymentAction
      ) {
        sessionStorage.removeItem(SESSION_KEY);
        setRedirecting(true);
        rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE);
        return;
      }
      // If payload has per-env items (envId) but targetEnvironments is missing, derive it so we group by key and show one row with correct env columns (avoids duplicate rows + wrong values)
      let payloadToSet = parsed;
      const hasPerEnvItems = (parsed.items as any[]).some((i: any) => i.metadata?.isEnvVar && (i.envId != null || i.env_id != null));
      if (hasPerEnvItems && (!parsed.targetEnvironments || parsed.targetEnvironments.length === 0)) {
        const envIds = new Set<number>();
        (parsed.items as any[]).forEach((i: any) => {
          if (i.metadata?.isEnvVar && (i.envId != null || i.env_id != null)) {
            const id = Number(i.envId ?? i.env_id);
            if (!Number.isNaN(id)) envIds.add(id);
          }
        });
        const derived = Array.from(envIds).sort((a, b) => a - b).map((id) => ({ id, environmentName: `Environment ${id}`, type: 'prod' as const }));
        payloadToSet = { ...parsed, targetEnvironments: derived };
      }
      setPayload(payloadToSet);
    } catch {
      sessionStorage.removeItem(SESSION_KEY);
      setRedirecting(true);
      rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE);
    }
  }, [rc.uiRouter, toast]);

  const handleCancel = useCallback(() => {
    // Keep SESSION_KEY so ConfigComparePage can fall back to re-fetch when IndexedDB fails (e.g. private browsing)
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE, { restoreCompare: true });
  }, [rc.uiRouter]);

  const handleConfirm = useCallback(async () => {
    if (!payload) return;
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_APPROVED, {
      deployment_action: payload.deploymentAction,
      item_count: payload.items.length,
    });
    setIsConfirming(true);
    try {
        // Use current payload.items (Review page is read-only; no edits). Items must carry full metadata (including tags) to align with compare API.
      const apiPayload = {
        projectId: payload.projectId,
        items: payload.items,
        sourceEnvId: payload.sourceEnvId,
        targetEnvId: payload.targetEnvId,
      };
      if (payload.deploymentAction === 'PUSH') {
        const resp = await PilotApis.pushConfigToolsSync(rcRef.current, apiPayload);
        if (resp.error) {
          const msg =
            typeof resp.error === 'string'
              ? resp.error
              : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Push failed');
          const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_FAILED, {
            deployment_action: payload.deploymentAction,
            error_type: 'api_error',
            severity: 'critical',
          });
          toast.error(`${msg}${suffix}`.trim());
          return;
        }
        const newSyncId = resp.data?.syncId ? String(resp.data.syncId) : null;
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_EXECUTED, {
          deployment_action: payload.deploymentAction,
          item_count: payload.items.length,
        });
        toast.success(newSyncId ? `Push started (syncId: ${newSyncId}).` : 'Push started.');
      } else {
        // Backend always returns ZIP file (binary data)
        setIsDownloadingZip(true);
        try {
          // Target environments: use payload.targetEnvironments or derive from payload.targetEnvId
          const targetEnvs =
            payload.targetEnvironments && payload.targetEnvironments.length > 0
              ? payload.targetEnvironments
              : [
                  {
                    id: payload.targetEnvId,
                    environmentName: payload.targetEnvLabel || `Environment ${payload.targetEnvId}`,
                    type: 'prod',
                  },
                ];

          const shellBundlePayload = buildShellBundlePayload(
            payload.items,
            targetEnvs,
            payload.sourceEnvId,
            payload.projectId
          );

          const resp = await PilotApis.createConfigToolsShellBundle(rcRef.current, shellBundlePayload);

          if (resp.error) {
            const msg =
              typeof resp.error === 'string'
                ? resp.error
                : (resp.error.message || (resp.error.code ? `${resp.error.code}` : '') || 'Create PR (shell bundle) failed');
            const suffix = typeof resp.error === 'object' && resp.error?.code ? ` (${resp.error.code})` : '';
            setIsDownloadingZip(false);
            rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_FAILED, {
              deployment_action: payload.deploymentAction,
              error_type: 'api_error',
              severity: 'critical',
            });
            toast.error(`${msg}${suffix}`.trim());
            return;
          }
          
          // Backend returned ZIP file as binary blob
          if (!resp.data) {
            setIsDownloadingZip(false);
            toast.error('No data received from server.');
            return;
          }
          
          // Ensure we have a Blob
          let zipBlob: Blob | null = null;
          if (resp.data instanceof Blob) {
            zipBlob = resp.data;
          } else if (resp.data instanceof ArrayBuffer) {
            zipBlob = new Blob([resp.data], { type: 'application/zip' });
          } else if (typeof resp.data === 'string') {
            // If it's a string, it might be base64 or JSON error
            try {
              const parsed = JSON.parse(resp.data);
              if (parsed.error || parsed.message) {
                throw new Error(parsed.error || parsed.message || 'Server returned error');
              }
            } catch (e) {
              if (e instanceof Error && e.message.includes('Server returned error')) {
                throw e;
              }
              // Not JSON, treat as binary string
              zipBlob = new Blob([resp.data], { type: 'application/zip' });
            }
          } else {
            zipBlob = new Blob([resp.data], { type: 'application/zip' });
          }
          
          if (!zipBlob) {
            setIsDownloadingZip(false);
            toast.error('Failed to process zip file from server.');
            return;
          }
          
          if (zipBlob.size === 0) {
            setIsDownloadingZip(false);
            toast.error('Received empty zip file from server.');
            return;
          }
          
          // Extract filename from Content-Disposition header
          let filename = `shell-bundle-${new Date().getTime()}.zip`;
          if (resp.headers) {
            const contentDisposition =
              resp.headers['content-disposition'] ||
              resp.headers['Content-Disposition'] ||
              (typeof resp.headers.get === 'function' ? resp.headers.get('content-disposition') : null);

            if (contentDisposition && typeof contentDisposition === 'string') {
              const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (match && match[1]) {
                filename = match[1].replace(/['"]/g, '').trim();
              }
            }
          }
          
          // Download the zip file
          const link = document.createElement('a');
          link.href = URL.createObjectURL(zipBlob);
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);

          setIsDownloadingZip(false);
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_EXECUTED, {
            deployment_action: payload.deploymentAction,
            item_count: payload.items.length,
          });
          toast.success(`Shell bundle zip downloaded: ${filename}`);

          // Don't navigate away immediately - let user see the success message
          sessionStorage.removeItem(SESSION_KEY);
          return; // Stay on the page
        } catch (error) {
          setIsDownloadingZip(false);
          const errorMsg = error instanceof Error ? error.message : 'Unknown error';
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.CONFIG_PROMOTION_FAILED, {
            deployment_action: payload.deploymentAction,
            error_type: 'api_error',
            severity: 'critical',
          });
          toast.error(`Failed to download shell bundle zip: ${errorMsg}`);
        }
      }
      sessionStorage.removeItem(SESSION_KEY);
      rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE);
    } finally {
      setIsConfirming(false);
    }
  }, [payload, toast, rc.uiRouter]);

  type TableRow = { configType: string; configKey: string; level: string; sourceValue: string; targetValue: string; status: string; targets?: TargetEntry[] };

  /** Section order for tabs (same semantics as Compare page). */
  const SECTION_ORDER: SectionKey[] = ['configs', 'stringResources', 'environmentConfigs', 'environmentStrings'];

  /** Build sections from all payload items: by category + env split (Environment Configs / Strings). */
  const sections = useMemo((): Record<string, TableRow[]> => {
    if (!payload) return {};
    const acc: Record<string, TableRow[]> = {};
    // Use payload.targetEnvironments when present; otherwise derive from items with envId so we group by key and show one row with env columns (avoids duplicate rows + wrong values)
    const hasExplicitTargetEnvs = payload.targetEnvironments && payload.targetEnvironments.length > 0;
    const effectiveTargetEnvironments: TargetEnvironment[] = hasExplicitTargetEnvs
      ? payload.targetEnvironments!
      : (() => {
          const envIds = new Set<number>();
          (payload.items as any[]).forEach((it: any) => {
            if (it.metadata?.isEnvVar && (it.envId != null || it.env_id != null)) {
              const id = Number(it.envId ?? it.env_id);
              if (!Number.isNaN(id)) envIds.add(id);
            }
          });
          return Array.from(envIds).sort((a, b) => a - b).map((id) => ({ id, environmentName: `Environment ${id}`, type: 'prod' as const }));
        })();
    const useEnvGrouping = effectiveTargetEnvironments.length > 0;

    // For environmentConfigs and environmentStrings, group by (configType, configKey, levelDisplay) for uniqueness
    const envItemsByKey = new Map<string, Map<number, any>>(); // Map<groupKey, Map<envId, item>>
    
    payload.items.forEach((it) => {
      const configType = it.metadata?.configType ?? it.category ?? '—';
      const configKey = it.metadata?.configKey ?? it.key;
      const levelDisplay = it.metadata?.levelDisplay ?? formatLevelDisplay(it.metadata ?? {}, '—');
      const groupKey = `${configType}::${configKey}::${levelDisplay}`;
      
      // Check if this is an environment variable item
      if (it.metadata?.isEnvVar === true) {
        const hasEnvId = (it as any).envId != null || (it as any).env_id != null;
        const isEnvItem =
          it.category === 'environmentConfigs' ||
          it.category === 'environmentStrings' ||
          (hasEnvId && useEnvGrouping);

        // For environment items with targetEnvironments (or derived), group by (configType, configKey, levelDisplay) and envId
        if (isEnvItem && useEnvGrouping) {
          const rawEnvId = (it as any).envId ?? (it as any).env_id;
          const envId = rawEnvId != null ? Number(rawEnvId) : (effectiveTargetEnvironments[0]?.id != null ? Number(effectiveTargetEnvironments[0].id) : NaN);
          if (!Number.isNaN(envId) && groupKey) {
            if (!envItemsByKey.has(groupKey)) {
              envItemsByKey.set(groupKey, new Map());
            }
            const envMap = envItemsByKey.get(groupKey)!;
            // First-write wins: do not overwrite if this env already has a value (preserves user edits from Compare page when multiple levels/items share the same key)
            if (!envMap.has(envId)) envMap.set(envId, it);
            // Do NOT merge targets for per-env items: each env's value comes from its own payload item (with user edits).
            // Merging targets would overwrite prod with OLD API values when we process the nonprod item first.
          }
        } else {
          // Environment variable item but no targetEnvironments or not a recognized env category
          // Fall through to regular processing
          const cat = String(it.category ?? '').toLowerCase();
          const section = cat.includes('string') ? 'environmentStrings' : 'environmentConfigs';
          const row: TableRow = {
            configType: it.metadata?.configType ?? it.category ?? '—',
            configKey: extractConfigKeyForDisplay(configKey),
            level: formatLevelDisplay(it.metadata),
            sourceValue: safeStringify(it.sourceValue),
            targetValue: safeStringify(it.targetValue),
            status: String(it.status ?? '—').toLowerCase(),
            ...(it.targets?.length ? { targets: it.targets } : {}),
          };
          if (!acc[section]) acc[section] = [];
          acc[section].push(row);
        }
      } else {
        // Fallback: env category with targets but no isEnvVar - will be handled below to build envValues (so prod column etc. show)
        const isEnvCategory = it.category === 'environmentConfigs' || it.category === 'environmentStrings';
        const hasTargets = payload.targetEnvironments?.length && Array.isArray((it as any).targets) && (it as any).targets.length > 0;
        if (isEnvCategory && hasTargets) {
          // Handled in fallback below - skip adding to acc here
        } else {
          // Regular item - create row directly
          const row: TableRow = {
            configType: it.metadata?.configType ?? it.category ?? '—',
            configKey,
            level: formatLevelDisplay(it.metadata),
            sourceValue: safeStringify(it.sourceValue),
            targetValue: safeStringify(it.targetValue),
            status: String(it.status ?? '—').toLowerCase(),
            ...(it.targets?.length ? { targets: it.targets } : {}),
          };
          const section = String(it.category ?? 'configs');
          if (!acc[section]) acc[section] = [];
          acc[section].push(row);
        }
      }
    });

    // Fallback: items with targets array but no isEnvVar (e.g. legacy payload) - build envValues so all columns (including prod) show
    if (useEnvGrouping) {
      payload.items.forEach((it) => {
        const isEnvCategory = it.category === 'environmentConfigs' || it.category === 'environmentStrings';
        const hasTargets = Array.isArray((it as any).targets) && (it as any).targets.length > 0;
        if (!it.metadata?.isEnvVar && isEnvCategory && hasTargets) {
          const ct = it.metadata?.configType ?? it.category ?? '—';
          const ck = it.metadata?.configKey ?? it.key;
          const ld = it.metadata?.levelDisplay ?? formatLevelDisplay(it.metadata ?? {}, '—');
          const groupKey = `${ct}::${ck}::${ld}`;
          if (!ck) return;
          if (!envItemsByKey.has(groupKey)) envItemsByKey.set(groupKey, new Map());
          const envMap = envItemsByKey.get(groupKey)!;
          for (const t of (it as any).targets) {
            const envId = t.envId ?? t.env_id;
            if (envId != null && envId !== undefined)
              envMap.set(Number(envId), { ...it, targetValue: t.targetValue ?? t.target_value });
          }
        }
      });
    }

    // Process environment items - create one row per (configType, configKey, levelDisplay) with values for each environment
    envItemsByKey.forEach((envMap, groupKey) => {
      const firstItem = Array.from(envMap.values())[0];
      if (!firstItem) return;
      const configType = firstItem.metadata?.configType ?? firstItem.category ?? '—';
      const rawConfigKey = firstItem.metadata?.configKey ?? firstItem.key;
      const configKey = extractConfigKeyForDisplay(rawConfigKey);
      const levelDisplay = firstItem.metadata?.levelDisplay ?? formatLevelDisplay(firstItem.metadata ?? {}, '—');
      
      const row: TableRow = {
        configType,
        configKey,
        level: levelDisplay,
        sourceValue: safeStringify(firstItem.sourceValue),
        targetValue: '', // Will be populated per environment in display
        status: String(firstItem.status ?? '—').toLowerCase(),
      };
      
      // Store environment values in the row (we'll use a custom property)
      (row as any).envValues = new Map<number, string>();
      envMap.forEach((item, envId) => {
        (row as any).envValues.set(Number(envId), safeStringify(item.targetValue));
      });
      
      const section = firstItem.category === 'environmentStrings' ? 'environmentStrings' : 'environmentConfigs';
      if (!acc[section]) acc[section] = [];
      acc[section].push(row);
    });
    
    return acc;
  }, [payload]);

  /** Ordered section keys that have at least one row (for tabs). */
  const sectionKeysWithRows = useMemo(() => {
    const ordered = SECTION_ORDER.filter((k) => (sections[k]?.length ?? 0) > 0);
    const rest = Object.keys(sections).filter((k) => !SECTION_ORDER.includes(k));
    return [...ordered, ...rest];
  }, [sections]);

  const currentSectionRows = useMemo(() => sections[sectionKey] ?? [], [sections, sectionKey]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let rows = currentSectionRows;
    if (filterStatus.length > 0) {
      rows = rows.filter((r) => filterStatus.includes(r.status));
    }
    if (filterLevel.length > 0) {
      rows = rows.filter((r) => filterLevel.includes(r.level));
    }
    if (filterConfigType.length > 0) {
      rows = rows.filter((r) => filterConfigType.includes(r.configType));
    }
    if (q) {
      rows = rows.filter(
        (r) =>
          r.configType.toLowerCase().includes(q) ||
          r.configKey.toLowerCase().includes(q) ||
          r.level.toLowerCase().includes(q) ||
          r.sourceValue.toLowerCase().includes(q) ||
          r.targetValue.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [currentSectionRows, searchQuery, filterStatus, filterLevel, filterConfigType]);

  /** Filtered counts per section for tab labels (honors search + filters). */
  const filteredSectionCounts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const counts: Record<string, number> = {};
    sectionKeysWithRows.forEach((k) => {
      let rows = sections[k] ?? [];
      if (filterStatus.length > 0) rows = rows.filter((r) => filterStatus.includes(r.status));
      if (filterLevel.length > 0) rows = rows.filter((r) => filterLevel.includes(r.level));
      if (filterConfigType.length > 0) rows = rows.filter((r) => filterConfigType.includes(r.configType));
      if (q) {
        rows = rows.filter(
          (r) =>
            r.configType.toLowerCase().includes(q) ||
            r.configKey.toLowerCase().includes(q) ||
            r.level.toLowerCase().includes(q) ||
            r.sourceValue.toLowerCase().includes(q) ||
            r.targetValue.toLowerCase().includes(q)
        );
      }
      counts[k] = rows.length;
    });
    return counts;
  }, [sections, sectionKeysWithRows, searchQuery, filterStatus, filterLevel, filterConfigType]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const filterOptions = useMemo(() => {
    const statuses = new Set<string>();
    const levels = new Set<string>();
    const configTypes = new Set<string>();
    currentSectionRows.forEach((r) => {
      statuses.add(r.status);
      levels.add(r.level);
      configTypes.add(r.configType);
    });
    return {
      statuses: Array.from(statuses).filter(Boolean).sort(),
      levels: Array.from(levels).filter(Boolean).sort(),
      configTypes: Array.from(configTypes).filter(Boolean).sort(),
    };
  }, [currentSectionRows]);

  const resetAllFilters = useCallback(() => {
    setFilterStatus([]);
    setFilterLevel([]);
    setFilterConfigType([]);
    setSearchQuery('');
    setPage(1);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [sectionKey]);

  /** Set initial or fallback section when payload/sections change. */
  useEffect(() => {
    if (sectionKeysWithRows.length === 0) return;
    if (!sectionKey || !sections[sectionKey]?.length) {
      setSectionKey(sectionKeysWithRows[0]);
    }
  }, [payload, sectionKeysWithRows, sectionKey, sections]);

  if (redirecting || !payload) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const actionLabel = payload.deploymentAction === 'PUSH' ? 'Confirm & Push Configs' : 'Confirm & Create PR';
  const sourceLabel = payload.sourceEnvLabel || `Env ${payload.sourceEnvId}`;
  const targetLabel = payload.targetEnvLabel || `Environment ${payload.targetEnvId}`;
  const projectName = payload.projectName ?? '';
  const targetEnvironments = payload.targetEnvironments ?? [];
  const isDualTarget = targetEnvironments.length >= 2;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: 12 }}>
      {/* Page header - expected: title, subtitle "Final verification...", Back to Selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Review & Confirm Config Promotion
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Final verification of environment-specific values before deployment.
          </Typography>
        </Box>
        <StudioButton variant="outline" onClick={handleCancel} disabled={isConfirming} startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}>
          Back to Selection
        </StudioButton>
      </Box>

      {/* Single summary section: Flow + Action, then yellow confirmation banner (clean & structured) */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 0, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', overflow: 'hidden' }}>
        <CardContent sx={{ pt: 2, pb: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Flow:{' '}
                </Typography>
                <Box component="span" sx={{ px: 1.5, py: 0.5, borderRadius: 999, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.200', color: 'text.primary', fontSize: 12, fontWeight: 600 }}>
                  {sourceLabel}
                </Box>
                <Typography component="span" sx={{ fontSize: 14, color: 'text.secondary' }}>→</Typography>
                <Box component="span" sx={{ px: 1.5, py: 0.5, borderRadius: 999, border: '1px solid', borderColor: 'primary.main', bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 12, fontWeight: 600 }}>
                  {targetLabel}
                </Box>
              </Box>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Action:{' '}
                </Typography>
                <SendIcon sx={{ fontSize: 16, color: 'action.active' }} />
                <Typography variant="body2">{payload.deploymentAction === 'PUSH' ? 'Direct Push' : 'Pull Request'}</Typography>
              </Box>
            </Box>
            <Box sx={{ py: 1.5, px: 2, bgcolor: '#ffd3a3', border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0, mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                Confirm values carefully: promotion will overwrite existing configurations in <strong>{targetLabel}</strong>.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Environment Related Configs: Tabs (Environment Configs / Environment Strings), search, filters, table */}
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <CardHeader
          sx={{ textAlign: 'left', bgcolor: 'background.paper' }}
          title={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <FilterListIcon sx={{ fontSize: 20, color: 'action.active' }} />
              <Typography sx={{ fontWeight: 700 }}>Staged items by category</Typography>
            </Box>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Staged items from {sourceLabel} to {targetLabel}.
          </Typography>

          <Tabs
            value={sectionKeysWithRows.includes(sectionKey) ? sectionKey : (sectionKeysWithRows[0] ?? '')}
            onChange={(_, v) => setSectionKey(String(v))}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}
          >
            {sectionKeysWithRows.map((k) => {
              const count = filteredSectionCounts[k] ?? sections[k]?.length ?? 0;
              const label =
                k === 'environmentConfigs'
                  ? 'Environment Configs'
                  : k === 'environmentStrings'
                    ? 'Environment Strings'
                    : k;
              return <Tab key={k} value={k} label={`${label} (${count})`} />;
            })}
          </Tabs>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
              <SearchIcon sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 20, color: 'action.active' }} />
              <input
                className={studioStyles.input}
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                style={{ paddingLeft: 36, width: '100%' }}
              />
            </Box>
            <PromotionFilter
              filterOptions={{
                statuses: filterOptions.statuses,
                levels: filterOptions.levels,
                configTypes: filterOptions.configTypes,
              }}
              filterStatus={filterStatus}
              filterLevel={filterLevel}
              filterConfigType={filterConfigType}
              onFilterStatusChange={(v) => setFilterStatus(v)}
              onFilterLevelChange={(v) => setFilterLevel(v)}
              onFilterConfigTypeChange={(v) => setFilterConfigType(v)}
              onResetAll={resetAllFilters}
              onPageReset={() => setPage(1)}
              topChips={
                searchQuery ? (
                  <Chip
                    size="small"
                    label={`Search: "${searchQuery}"`}
                    onDelete={() => {
                      setSearchQuery('');
                      setPage(1);
                    }}
                    sx={{ height: 28 }}
                  />
                ) : undefined
              }
            />
          </Box>

          <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <Box component="table" sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, minWidth: 700 }}>
            <colgroup>
              {!(sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string')) && <col style={{ width: '12%' }} />}
              <col style={{ width: (sectionKey === 'environmentConfigs' || sectionKey === 'environmentStrings') && (payload.targetEnvironments?.length ?? 0) > 1 ? '18%' : (sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? '22%' : '16%') }} />
              <col style={{ width: sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? '18%' : '14%' }} />
              <col style={{ width: '16%' }} />
              {(sectionKey === 'environmentConfigs' || sectionKey === 'environmentStrings') && payload.targetEnvironments && payload.targetEnvironments.length > 0 ? (
                payload.targetEnvironments.map((te) => (
                  <col key={te.id} style={{ width: '14%' }} />
                ))
              ) : isDualTarget ? (
                targetEnvironments.map((te) => (
                  <col key={te.id} style={{ width: '14%' }} />
                ))
              ) : (
                <col style={{ width: '18%' }} />
              )}
              <col style={{ width: 130 }} />
            </colgroup>
            <Box component="thead" sx={{ bgcolor: 'action.hover', '& tr th': { verticalAlign: 'top', textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid', borderColor: 'divider' } }}>
              <Box component="tr">
                {!(sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string')) && (
                  <Box component="th">
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4 }}>Config Type</Typography>
                  </Box>
                )}
                <Box component="th">
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4 }}>
                    {sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? 'String key' : 'Config Key'}
                  </Typography>
                </Box>
                <Box component="th">
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4 }}>Level</Typography>
                </Box>
                <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={`Source: ${sourceLabel}`}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4 }}>
                    Source:{' '}
                    {sourceLabel.includes(' (') ? (
                      <>
                        {sourceLabel.split(' (')[0]}
                        <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}> ({sourceLabel.split(' (')[1]}</Box>
                      </>
                    ) : (
                      sourceLabel
                    )}
                  </Typography>
                </Box>
                {(sectionKey === 'environmentConfigs' || sectionKey === 'environmentStrings') && payload.targetEnvironments && payload.targetEnvironments.length > 0 ? (
                  payload.targetEnvironments.map((te) => {
                    const label = getEnvListDisplayLabel(te, projectName);
                    return (
                      <Box key={te.id} component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={label}>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {label}
                        </Typography>
                      </Box>
                    );
                  })
                ) : isDualTarget ? (
                  targetEnvironments.map((te) => {
                    const label = getEnvListDisplayLabel(te, projectName);
                    return (
                      <Box key={te.id} component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={label}>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {label}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Box component="th" sx={{ overflow: 'hidden', minWidth: 0 }} title={`Target: ${targetLabel}`}>
                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4 }}>
                      Target:{' '}
                      {targetLabel.includes(' (') ? (
                        <>
                          {targetLabel.split(' (')[0]}
                          <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}> ({targetLabel.split(' (')[1]}</Box>
                        </>
                      ) : (
                        targetLabel
                      )}
                    </Typography>
                  </Box>
                )}
                <Box component="th">
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1.4 }}>Status</Typography>
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {pagedRows.length === 0 ? (
                <Box component="tr">
                  <Box 
                    component="td" 
                    colSpan={
                      (sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string') ? 4 : 5) +
                      ((sectionKey === 'environmentConfigs' || sectionKey === 'environmentStrings') && payload.targetEnvironments && payload.targetEnvironments.length > 0
                        ? payload.targetEnvironments.length
                        : isDualTarget ? targetEnvironments.length : 1)
                    }
                    sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {filteredRows.length === 0 && (searchQuery || filterStatus.length > 0 || filterLevel.length > 0 || filterConfigType.length > 0) ? 'No items match your filters.' : 'No items.'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                pagedRows.map((row, idx) => (
                  <Box component="tr" key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    {!(sectionKey === 'stringResources' || sectionKey.toLowerCase().includes('string')) && (
                      <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', verticalAlign: 'middle', overflow: 'hidden', minWidth: 0 }}>
                        <Tooltip title={row.configType} placement="top" enterDelay={300}>
                          <Box component="span" sx={{ display: 'block', px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.200', color: 'text.secondary', fontSize: theme.typography.body2.fontSize, fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                            {row.configType}
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                    <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', verticalAlign: 'middle', overflow: 'hidden', minWidth: 0 }}>
                      <Tooltip title={row.configKey} placement="top" enterDelay={300}>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{
                            display: 'block',
                            fontFamily: 'monospace',
                            fontSize: theme.typography.body2.fontSize,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}
                        >
                          {row.configKey}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', verticalAlign: 'middle', overflow: 'hidden', minWidth: 0 }}>
                      <Tooltip title={row.level} placement="top" enterDelay={300}>
                        <Box component="span" sx={{ display: 'block', px: 1, py: 0.25, borderRadius: 999, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', fontSize: theme.typography.body2.fontSize, color: 'text.secondary', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                          {row.level}
                        </Box>
                      </Tooltip>
                    </Box>
                    <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', fontSize: theme.typography.body2.fontSize, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left', verticalAlign: 'middle' }}>
                      {row.sourceValue}
                    </Box>
                    {(sectionKey === 'environmentConfigs' || sectionKey === 'environmentStrings') && payload.targetEnvironments && payload.targetEnvironments.length > 0 && (row as any).envValues ? (
                      payload.targetEnvironments.map((te) => {
                        const teIdNum = Number(te.id);
                        const displayValue = (row as any).envValues?.get(teIdNum) ?? (row as any).envValues?.get(te.id) ?? '';
                        return (
                          <Box key={te.id} component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', verticalAlign: 'middle' }}>
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                display: 'block',
                              }}
                            >
                              {displayValue || '—'}
                            </Typography>
                          </Box>
                        );
                      })
                    ) : isDualTarget ? (
                      targetEnvironments.map((te, ti) => {
                        const teId = te.id != null ? Number(te.id) : te.id;
                        const t = row.targets?.find((x: any) => (x.envId ?? x.env_id) === teId || (x.envId ?? x.env_id) === te.id);
                        const tDisplay =
                          t != null && (t.targetValue !== null && typeof t.targetValue !== 'undefined')
                            ? safeStringify(t.targetValue)
                            : ti === 0
                              ? row.targetValue
                              : '—';
                        return (
                          <Box key={te.id} component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', fontSize: theme.typography.body2.fontSize, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left', verticalAlign: 'middle' }}>
                            {tDisplay}
                          </Box>
                        );
                      })
                    ) : (
                      <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', fontSize: theme.typography.body2.fontSize, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left', verticalAlign: 'middle' }}>
                        {row.targetValue}
                      </Box>
                    )}
                    <Box component="td" sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left', verticalAlign: 'middle' }}>
                      <Box
                        component="span"
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 999,
                          fontSize: 12,
                          border: '1px solid',
                          borderColor:
                            row.status === 'new' || row.status === 'added'
                              ? 'success.main'
                              : row.status === 'conflict'
                                ? 'error.main'
                                : row.status === 'modified'
                                  ? 'warning.main'
                                  : row.status === 'target_only'
                                    ? 'info.main'
                                    : 'divider',
                          color:
                            row.status === 'new' || row.status === 'added'
                              ? 'success.main'
                              : row.status === 'conflict'
                                ? 'error.main'
                                : row.status === 'modified'
                                  ? 'warning.main'
                                  : row.status === 'target_only'
                                    ? 'info.main'
                                    : 'text.secondary',
                          fontWeight: 600,
                        }}
                      >
                        {row.status.toUpperCase()}
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredRows.length === 0 ? 0 : (page - 1) * rowsPerPage + 1} – {Math.min(page * rowsPerPage, filteredRows.length)} of {filteredRows.length} items
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>Rows:</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  sx={{ minWidth: 70, height: 32 }}
                >
                  {[10, 20, 50, 100, 300, 500].map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <StudioButton
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </StudioButton>
              {(() => {
                const start = totalPages <= 10 ? 1 : Math.max(1, Math.min(page - 5, totalPages - 9));
                return Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
                  const pageNum = start + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <StudioButton
                      key={pageNum}
                      variant={page === pageNum ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </StudioButton>
                  );
                });
              })()}
              <StudioButton
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </StudioButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {shellBundle?.shellBundleUrl && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              Shell bundle ready:{' '}
              {(() => {
                const base = String(process.env.REACT_APP_TOOL_API_URL || '').replace(/\/+$/, '');
                const rel = String(shellBundle.shellBundleUrl || '');
                const abs = rel.startsWith('http') ? rel : `${base}${rel.startsWith('/') ? '' : '/'}${rel}`;
                return (
                  <a href={abs} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                    Download script
                  </a>
                );
              })()}
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                sessionStorage.removeItem('config_tools_shell_bundle');
                rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE);
              }}
            >
              Back to Compare
            </Button>
          </Box>
        </Alert>
      )}

      {/* Fixed bottom bar: Cancel (left), Confirm & Push Configs with checkmark (right) */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 'var(--drawer-width, 270px)',
          right: 0,
          py: 2,
          px: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          transition: 'left 0.2s ease-in-out',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isConfirming && <CircularProgress size={20} sx={{ flexShrink: 0 }} />}
          <Typography variant="body2" color="text.secondary">
            You have selected {payload.items.length} item(s) to synchronize.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <StudioButton variant="outline" onClick={handleCancel} disabled={isConfirming}>
            Cancel
          </StudioButton>
          <PermissionGate require={UI_PAGES.CONFIG_COMPARE.ctas.sync_push.id} mode="disable">
            <StudioButton
              variant="primary"
              onClick={handleConfirm}
              disabled={isConfirming || isDownloadingZip}
              startIcon={(isConfirming || isDownloadingZip) ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <CheckCircleIcon sx={{ fontSize: 18, color: 'inherit' }} />}
            >
              {isDownloadingZip ? 'Downloading...' : actionLabel}
            </StudioButton>
          </PermissionGate>
        </Box>
      </Box>
    </Box>
  );
};
